import { useNavigate } from "react-router-dom";
import { ScoreBadge } from "@school-sales-ai/shared/components";
import type { InboxItem } from "@school-sales-ai/shared";
import { Avatar } from "@/components/Avatar";
import { CandidateCountChip } from "@/components/CandidateCountChip";
import { CardLabels } from "@/components/CardLabels";
import { ChannelIcon } from "@/components/ChannelIcon";
import { StagePill } from "@/components/StagePill";
import { useMarkHandoffAttended } from "@/hooks/useInbox";
import { senderLabel } from "@/lib/message-sender";
import { describeReplyWindow, formatRemaining } from "@/lib/reply-window";
import { timeAgo } from "@/lib/time-ago";
import { cn } from "@/lib/utils";
import { AssigneeBadge } from "@/components/lead-assignment/AssigneeBadge";

type Props = { item: InboxItem };

/**
 * The window as a row-sized chip, or null when it says nothing useful.
 *
 * Deliberately silent while the window is comfortably open and for tenants that
 * have none (Baileys): a chip on every row would be noise, and the Buzón's job
 * is to make the few actionable things stand out.
 */
function describeWindowChip(
  window: InboxItem["replyWindow"],
): { label: string; className: string } | null {
  const view = describeReplyWindow(window);
  if (view.state === "closing" && view.remainingMs !== null) {
    return {
      label: `Quedan ${formatRemaining(view.remainingMs)}`,
      className: "bg-amber-50 text-amber-700",
    };
  }
  if (view.state === "closed") {
    return { label: "Ventana cerrada", className: "bg-ink-100 text-ink-500" };
  }
  return null;
}

/**
 * How an open handoff reads on the row.
 *
 * The two types are not the same emergency, and the colour is what says so.
 * `user_requested` pauses the AI, so nobody at all is answering that person right
 * now — red. On `ai_initiated` the agent keeps talking, so the prospect is not
 * stranded, but it did promise them a human — amber.
 *
 * Same shape as describeWindowChip above, for the same reason: the row decides
 * nothing, it renders what it is handed.
 */
function describeHandoffChip(handoff: NonNullable<InboxItem["handoff"]>): {
  label: string;
  chipClassName: string;
  railClassName: string;
} {
  if (handoff.type === "user_requested") {
    return {
      label: "Pidió asesor · IA pausada",
      chipClassName: "bg-red-50 text-red-700",
      railClassName: "bg-red-500",
    };
  }
  return {
    label: "IA lo canalizó",
    chipClassName: "bg-amber-50 text-amber-700",
    railClassName: "bg-amber-500",
  };
}

export function InboxRow({ item }: Props): React.JSX.Element {
  const navigate = useNavigate();
  // `from` is what LeadDetail's resolveBackTarget reads to send the user back
  // here instead of to the pipeline.
  const open = (): void =>
    void navigate(`/leads/${item.id}`, { state: { from: "/buzon" } });

  // `lastChannel` used to be part of this guard because senderLabel needed it.
  // It is null only when there is no last message, in which case `lastRole` is
  // null too — so dropping it changes nothing about which rows show a sender.
  const sender = item.lastRole ? senderLabel(item.lastRole) : null;

  const windowChip = describeWindowChip(item.replyWindow);
  const handoffChip = item.handoff ? describeHandoffChip(item.handoff) : null;
  const markAttended = useMarkHandoffAttended();

  return (
    // A div, not a button: the row carries the label picker, and a dropdown
    // trigger button nested inside a button is invalid HTML. Same approach the
    // pipeline's TableView takes for its clickable rows — the interactive bits
    // inside stop propagation so they never also open the lead.
    <div
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          open();
        }
      }}
      className={cn(
        "flex w-full cursor-pointer items-start gap-3 border-b border-ink-100 px-4 py-3 text-left transition-colors hover:bg-ink-50",
        item.unread && "bg-lime/5",
      )}
    >
      {/* Unread rail. A dot in the flow would shift every other row by its width
          when it appears; a fixed-width slot keeps the columns still.

          An open handoff takes the same slot as a full-height bar instead of a
          dot: it is a property of the whole row, and it outranks unread — a lead
          waiting for a person is the more urgent fact even when someone has
          already glanced at it. */}
      <span className="mt-2 flex w-2 shrink-0 justify-center self-stretch">
        {handoffChip ? (
          <span
            className={cn("w-1 rounded-full", handoffChip.railClassName)}
            aria-label="Transferido a un asesor"
          />
        ) : (
          item.unread && (
            <span
              className="h-2 w-2 rounded-full bg-lime-deep"
              aria-label="No leído"
            />
          )
        )}
      </span>

      {/* Falls back to the session ref so an unnamed lead still gets stable
          initials and colour rather than a blank circle. */}
      <Avatar name={item.name ?? item.sessionRef} className="mt-0.5 shrink-0" />

      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span
            className={cn(
              "truncate text-sm text-ink-900",
              item.unread ? "font-semibold" : "font-medium",
            )}
          >
            {item.name ?? "—"}
          </span>
          <span className="shrink-0 font-mono text-xs text-ink-400">
            {item.sessionRef}
          </span>
        </div>

        <div className="mt-0.5 flex items-baseline gap-1.5 text-sm">
          {sender && (
            <span className="shrink-0 text-xs font-medium text-ink-400">
              {sender}:
            </span>
          )}
          <span
            className={cn(
              "truncate",
              item.unread ? "text-ink-900" : "text-ink-500",
            )}
          >
            {item.preview ?? "—"}
          </span>
        </div>

        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          {item.lastChannel && <ChannelIcon channel={item.lastChannel} />}
          <StagePill stage={item.stage} small />
          <ScoreBadge score={item.score} size="xs" />
          {/* Next to the score on purpose: both answer "how much is this lead
              worth", and two children is the one thing on this row the score
              does not already reflect. */}
          <CandidateCountChip count={item.candidateCount} />
          <AssigneeBadge assignedProfileId={item.assignedProfileId} className="max-w-[160px]" />
          {/* Waiting is derived: the prospect spoke last and nobody has
              answered. It stays on after the row is read — seen is not
              answered — and clears itself the moment anyone replies. */}
          {item.waiting && (
            <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">
              Esperando respuesta
            </span>
          )}
          {/* Only worth the row's space once it is actionable: a window with
              hours left is not news, one that is closing or closed changes what
              the advisor can do about this lead right now. */}
          {windowChip && (
            <span
              className={cn(
                "rounded-full px-2 py-0.5 text-[10px] font-medium",
                windowChip.className,
              )}
            >
              {windowChip.label}
            </span>
          )}
          {/* First among the chips: it is why this row is being looked at. Carries
              its own age, which is NOT the row's timestamp on the right — that one
              is the last message, and a handoff can sit open while the AI keeps
              chatting, so the two drift apart exactly when it matters. */}
          {handoffChip && item.handoff && (
            <span
              className={cn(
                "rounded-full px-2 py-0.5 text-[10px] font-medium",
                handoffChip.chipClassName,
              )}
            >
              {/* Lowercased: timeAgo is written to start a line ("Hace 3 h") and
                  this is the tail of a phrase. */}
              {handoffChip.label} · {timeAgo(item.handoff.requestedAt).toLowerCase()}
            </span>
          )}
          {/* Suppressed while the handoff chip is up: that chip already says "IA
              pausada" for the type that pauses, and two badges for one fact just
              crowd the row. */}
          {item.aiPaused && !handoffChip && (
            <span className="rounded-full bg-ink-100 px-2 py-0.5 text-[10px] font-medium text-ink-500">
              IA pausada
            </span>
          )}
          {/* Editable in place: tagging is triage, and triage is what this page
              is for. CardLabels stops propagation on its own controls so a click
              on a chip never also opens the lead. */}
          <CardLabels leadId={item.id} labels={item.labels} />
        </div>
      </div>

      <div className="flex shrink-0 flex-col items-end gap-1.5 pt-0.5">
        <span className="text-xs text-ink-400">{timeAgo(item.lastMessageAt)}</span>
        {/* The escape hatch for a handoff answered where this app cannot see it:
            a phone call, an email, a prospect who walked in — and the widget-only
            lead with no number, which can never receive a reply from the CRM at
            all. Without it the queue would keep those rows forever. */}
        {handoffChip && (
          <button
            type="button"
            disabled={markAttended.isPending}
            onClick={(e) => {
              // The row is itself a click target; without this, closing the
              // handoff would also navigate away from the list being triaged.
              e.stopPropagation();
              markAttended.mutate(item.id);
            }}
            className="rounded-md border border-ink-100 px-2 py-0.5 text-[10px] font-medium text-ink-500 transition-colors hover:bg-ink-50 hover:text-ink-900 disabled:opacity-50"
          >
            Atendido
          </button>
        )}
      </div>
    </div>
  );
}
